import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import "./UpdateInventoryManually.css";

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

const buildFormData = (drug) => ({
  id: drug.id || "",
  generic_name: drug.generic_name || "",
  brand_name: drug.brand_name || "",
  category: drug.category || "",
  strength: drug.strength || "",
  dosage_form: capitalize(drug.dosage_form),
  pack_size: drug.pack_size || "",
  unit: drug.unit || "",
  manufacturer: drug.manufacturer || "",
  description: drug.description || "",
  batch_number: drug.batch_number || "",
  quantity: drug.quantity ?? "",
  price: drug.price ?? "",
  cost_price: drug.cost_price != null ? String(drug.cost_price) : "",
  wholesale_price: drug.wholesale_price != null ? String(drug.wholesale_price) : "",
  expiration_date: drug.expiration_date || "",
  nafdac_number: drug.nafdac_number || "",
});

const UpdateInventoryManually = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const drugFromDashboard = location.state?.drugFromDashboard || {};

  const [formData, setFormData] = useState(buildFormData(drugFromDashboard));
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const bottomRef = useRef(null);

  // ── Re-fetch the item from API to get latest fields (including wholesale_price) ──
  // Navigation state may be stale if fetched before wholesale_price was added.
  useEffect(() => {
    const id = drugFromDashboard.id;
    if (!id) return;

    const fetchFresh = async () => {
      setIsFetching(true);
      try {
        const { data } = await api.get("/pharmacies/get_inventory", {
          params: { page: 1, limit: 1, query: drugFromDashboard.generic_name || drugFromDashboard.brand_name || "" },
        });
        // Find the exact item by ID in results
        const items = data?.data || [];
        const fresh = items.find((i) => i.id === id);
        if (fresh) {
          setFormData(buildFormData(fresh));
        }
      } catch {
        // Non-fatal — keep using navigation state data
      } finally {
        setIsFetching(false);
      }
    };

    fetchFresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // ── Live margin previews ──
  const sellingPrice = parseFloat(formData.price);
  const costPrice = parseFloat(formData.cost_price);
  const wholesalePrice = parseFloat(formData.wholesale_price);

  const showRetailMargin =
    formData.price !== "" && formData.cost_price !== "" &&
    !isNaN(sellingPrice) && !isNaN(costPrice) &&
    sellingPrice > 0 && costPrice >= 0;

  const showWholesaleMargin =
    formData.wholesale_price !== "" && formData.cost_price !== "" &&
    !isNaN(wholesalePrice) && !isNaN(costPrice) &&
    wholesalePrice > 0 && costPrice >= 0;

  const retailProfit = showRetailMargin ? sellingPrice - costPrice : null;
  const retailMarginPct =
    showRetailMargin && sellingPrice > 0
      ? ((retailProfit / sellingPrice) * 100).toFixed(1)
      : null;

  const wholesaleProfit = showWholesaleMargin ? wholesalePrice - costPrice : null;
  const wholesaleMarginPct =
    showWholesaleMargin && wholesalePrice > 0
      ? ((wholesaleProfit / wholesalePrice) * 100).toFixed(1)
      : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!formData.id) {
      setMessage({ type: "error", text: "Missing inventory ID." });
      return;
    }

    if (
      formData.cost_price !== "" &&
      (isNaN(parseFloat(formData.cost_price)) || parseFloat(formData.cost_price) < 0)
    ) {
      setMessage({ type: "error", text: "Cost price must be a valid positive number." });
      return;
    }

    if (
      formData.wholesale_price !== "" &&
      (isNaN(parseFloat(formData.wholesale_price)) || parseFloat(formData.wholesale_price) < 0)
    ) {
      setMessage({ type: "error", text: "Wholesale price must be a valid positive number." });
      return;
    }

    const payload = {
      id: formData.id,
      generic_name: formData.generic_name?.trim() || "",
      brand_name: formData.brand_name?.trim() || "",
      category: formData.category?.trim() || "",
      strength: formData.strength?.trim() || "",
      dosage_form: formData.dosage_form?.trim() || "",
      pack_size: formData.pack_size?.trim() || "",
      unit: formData.unit?.trim() || "",
      manufacturer: formData.manufacturer?.trim() || "",
      description: formData.description?.trim() || "",
      quantity: formData.quantity !== "" && !isNaN(formData.quantity) ? Number(formData.quantity) : 0,
      price: formData.price !== "" && !isNaN(formData.price) ? Number(formData.price) : 0,
      cost_price: formData.cost_price !== "" && !isNaN(formData.cost_price) ? parseFloat(formData.cost_price) : null,
      wholesale_price: formData.wholesale_price !== "" && !isNaN(formData.wholesale_price) ? parseFloat(formData.wholesale_price) : null,
      expiration_date: formData.expiration_date || null,
      nafdac_number: formData.nafdac_number?.trim() || "",
      batch_number: formData.batch_number?.trim() || "",
    };

    setIsLoading(true);
    try {
      await api.put("/pharmacies/update_inventory", payload);
      setMessage({ type: "success", text: "Inventory updated successfully!" });
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      setTimeout(() => navigate("/InventoryItem"), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const backendError =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : "Failed to update inventory.";
      setMessage({ type: "error", text: backendError });
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="update-inventory-dashboard">
      {!isMobile && <Sidebar />}
      <div className="update-inventory-main">
        <h2 className="update-title">🧾 Update Inventory</h2>

        {isFetching && (
          <p style={{ color: "#aaa", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            🔄 Loading latest data...
          </p>
        )}

        <form className="update-inventory-form" onSubmit={handleSubmit}>
          <input type="hidden" value={formData.id} />

          <label className="update-field-label">
            Provide at least one name <small>(Generic name or Brand name)</small>
          </label>

          <input type="text" placeholder="Generic Name (optional)" value={formData.generic_name} onChange={(e) => handleChange("generic_name", e.target.value)} disabled={isLoading} />
          <input type="text" placeholder="Brand Name (optional)" value={formData.brand_name} onChange={(e) => handleChange("brand_name", e.target.value)} disabled={isLoading} />
          <input type="text" placeholder="Category (optional)" value={formData.category} onChange={(e) => handleChange("category", e.target.value)} disabled={isLoading} />
          <input type="number" placeholder="Quantity (optional)" value={formData.quantity} onChange={(e) => handleChange("quantity", e.target.value)} disabled={isLoading} />

          {/* ── Selling Price ── */}
          <label className="update-field-label">
            Selling Price <small>(price charged to retail customers)</small>
          </label>
          <div className="update-inventory-price">
            <span>₦</span>
            <input type="number" placeholder="Selling Price (optional)" value={formData.price} min="0" step="0.01" onChange={(e) => handleChange("price", e.target.value)} disabled={isLoading} />
          </div>

          {/* ── Wholesale Price ── */}
          <label className="update-field-label">
            Wholesale Price <small>(optional — price charged to bulk/wholesale buyers)</small>
          </label>
          <div className="update-inventory-price">
            <span>₦</span>
            <input type="number" placeholder="Wholesale Price (optional)" value={formData.wholesale_price} min="0" step="0.01" onChange={(e) => handleChange("wholesale_price", e.target.value)} disabled={isLoading} />
          </div>

          {/* ── Cost Price ── */}
          <label className="update-field-label">
            Cost Price <small>(optional — used to calculate profit in POS analytics)</small>
          </label>
          <div className="update-inventory-price">
            <span>₦</span>
            <input type="number" placeholder="Cost Price (optional)" value={formData.cost_price} min="0" step="0.01" onChange={(e) => handleChange("cost_price", e.target.value)} disabled={isLoading} />
          </div>

          {/* ── Retail margin preview ── */}
          {showRetailMargin && (
            <p style={{ color: retailProfit >= 0 ? "#2e7d32" : "#c62828", fontWeight: 600, fontSize: "0.85rem", margin: "2px 0 6px" }}>
              {retailProfit >= 0 ? "📈" : "📉"} Retail profit per unit: ₦{retailProfit.toFixed(2)} ({retailMarginPct}% margin)
            </p>
          )}

          {/* ── Wholesale margin preview ── */}
          {showWholesaleMargin && (
            <p style={{ color: wholesaleProfit >= 0 ? "#1565c0" : "#c62828", fontWeight: 600, fontSize: "0.85rem", margin: "2px 0 10px" }}>
              {wholesaleProfit >= 0 ? "📦" : "📉"} Wholesale profit per unit: ₦{wholesaleProfit.toFixed(2)} ({wholesaleMarginPct}% margin)
            </p>
          )}

          {/* ── Expiration Date ── */}
          <label className="update-field-label">
            Expiration Date <small>(optional)</small>
          </label>
          <input type="date" value={formData.expiration_date} onChange={(e) => handleChange("expiration_date", e.target.value)} disabled={isLoading} />

          <input type="text" placeholder="NAFDAC Number (optional)" value={formData.nafdac_number} onChange={(e) => handleChange("nafdac_number", e.target.value)} disabled={isLoading} />
          <input type="text" placeholder="Strength (optional)" value={formData.strength} onChange={(e) => handleChange("strength", e.target.value)} disabled={isLoading} />

          <select value={formData.dosage_form} onChange={(e) => handleChange("dosage_form", e.target.value)} disabled={isLoading}>
            <option value="">Select Dosage Form (optional)</option>
            {["Tablet","Capsule","Syrup","Suspension","Injection","Cream","Ointment","Gel","Lotion","Inhaler","Drops","Suppository","Patch"].map((form) => (
              <option key={form} value={form}>{form}</option>
            ))}
          </select>

          <input type="text" placeholder="Batch Number (optional)" value={formData.batch_number} onChange={(e) => handleChange("batch_number", e.target.value)} disabled={isLoading} />
          <input type="text" placeholder="Pack Size (optional)" value={formData.pack_size} onChange={(e) => handleChange("pack_size", e.target.value)} disabled={isLoading} />
          <input type="text" placeholder="Unit (optional)" value={formData.unit} onChange={(e) => handleChange("unit", e.target.value)} disabled={isLoading} />
          <input type="text" placeholder="Manufacturer (optional)" value={formData.manufacturer} onChange={(e) => handleChange("manufacturer", e.target.value)} disabled={isLoading} />
          <input type="text" placeholder="Description (optional)" value={formData.description} onChange={(e) => handleChange("description", e.target.value)} disabled={isLoading} />

          <button type="submit" className="update-btn" disabled={isLoading || isFetching}>
            {isLoading ? <><span className="button-spinner"></span>Updating...</> : "Update Inventory"}
          </button>

          <div ref={bottomRef}>
            {message.text && (
              <div className={message.type === "success" ? "update-inventory-success" : "update-inventory-error"} style={{ marginTop: "1rem" }}>
                {message.text}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateInventoryManually;